// @flow
import type { MessageDescriptor } from 'react-intl';
import type { ComponentType } from 'react';

import GlobalSettingsPage from './GlobalSettingsPage';
import localizedStrings from 'util/localizedStrings';
import BackupsPage from 'view/Pages/BackupsPage';
import CatalogsPage, {
  CatalogDetailsPage,
  CatalogIngestPage,
  CatalogingOptionsForm,
  CatalogManagedUploadForm,
  CatalogPreviewsForm,
  CatalogSmartKeywordsPage,
  CatalogUsersPage,
  FieldsPage,
} from 'view/Pages/CatalogsPage';
import CertsPage from 'view/Pages/CertsPage/CertsPage';
import DirectoryServicesPage from 'view/Pages/DirectoryServicesPage';
import StatusPage from 'view/Pages/StatusPage/StatusPage';
import RolesPage from 'view/Pages/RolesPage';
import UsersPage from 'view/Pages/UsersPage';
// import AnalyticsPage from 'view/Pages/AnalyticsPage';
import ReportsPage from 'view/Pages/ReportsPage';
import NetPublishPage from 'view/Pages/NetPublishPage';
import MobilePage from 'view/Pages/GlobalSettingsPages/MobilePage';
import ServerLocationsPage from 'view/Pages/GlobalSettingsPages/ServerLocationsPage';
import SingleSignOnCredsPage from 'view/Pages/GlobalSettingsPages/SingleSignOnCredsPage';
import EmailNotificationsPage from 'view/Pages/GlobalSettingsPages/EmailNotificationsPage';
import MediaEnginesPage from 'view/Pages/GlobalSettingsPages/MediaEnginesPage';
import LoginCustomizationPage from 'view/Pages/GlobalSettingsPages/LoginCustomizationPage';
import PortsPage from 'view/Pages/GlobalSettingsPages/PortsPage';
import LoggingControls from 'view/Pages/StatusPage/LoggingControls';

export type ContentsType = { component: ComponentType<any> };
export type PageData = {
  badge?: boolean,
  component: ComponentType<any>,
  label: MessageDescriptor,
  route: string,
  visibleForCatalogAdmin?: boolean,
  contents?: ContentsType,
  dataTestName?: string,
};

export const mainPages: Array<PageData> = [
  {
    component: StatusPage,
    label: localizedStrings.NAV_STATUS,
    route: '/status',
    dataTestName: 'status',
  },
  {
    component: CatalogsPage,
    label: localizedStrings.NAV_CATALOGS,
    route: '/catalogs',
    visibleForCatalogAdmin: true,
    dataTestName: 'catalogs',
  },
  {
    component: UsersPage,
    label: localizedStrings.NAV_USERS,
    route: '/users',
    dataTestName: 'users',
  },
  {
    component: RolesPage,
    label: localizedStrings.NAV_ACCESS_LEVELS,
    route: '/roles',
    dataTestName: 'roles',
  },
  {
    component: DirectoryServicesPage,
    label: localizedStrings.DIRECTORY_SERVICES,
    route: '/directoryServices',
    dataTestName: 'directory-services',
  },
  {
    component: ReportsPage,
    label: localizedStrings.NAV_REPORTING,
    route: '/reports',
    visibleForCatalogAdmin: true,
    dataTestName: 'reports',
  },
  {
    component: NetPublishPage,
    label: localizedStrings.NAV_NETPUBLISH_SETTINGS,
    route: '/NetPublish',
    visibleForCatalogAdmin: true,
    dataTestName: 'netpublish',
  },
  {
    component: BackupsPage,
    label: localizedStrings.NAV_BACKUPS,
    route: '/backups',
    dataTestName: 'backups',
  },
  {
    component: GlobalSettingsPage,
    label: localizedStrings.NAV_GLOBAL_SETTINGS,
    route: '/globalSettings',
    dataTestName: 'global-settings',
  },
];

export const catalogPages = [
  {
    label: localizedStrings.CATALOG_DETAILS_DATABASE_TAB,
    subroute: 'general',
    contents: { component: CatalogDetailsPage },
    dataTestName: 'catalog-details-general',
  },
  {
    label: localizedStrings.NAV_USERS,
    subroute: 'userMembership',
    contents: { component: CatalogUsersPage },
    dataTestName: 'catalog-details-users',
  },
  {
    label: localizedStrings.CATALOG_INGEST_FIELDS_LABEL,
    subroute: 'fields',
    contents: { component: FieldsPage },
    dataTestName: 'catalog-details-fields',
  },
  {
    label: localizedStrings.CATALOG_FILES_TAB_TITLE,
    subroute: 'ingest',
    contents: { component: CatalogIngestPage },
    dataTestName: 'catalog-details-ingest',
  },
  {
    label: localizedStrings.CATALOG_SK_TAB_TITLE,
    subroute: 'smartKeywords',
    contents: { component: CatalogSmartKeywordsPage },
    dataTestName: 'catalog-details-smart-keywords',
  },
];

export const ingestForms = [
  {
    label: localizedStrings.CATALOG_OPTIONS_TAB_TITLE,
    route: 'catalogingOptions',
    component: CatalogingOptionsForm,
    dataTestName: 'catalog-details-ingest-cataloging-options',
  },
  {
    label: localizedStrings.CATALOG_INGEST_TAB_TITLE,
    route: 'managedUploads',
    component: CatalogManagedUploadForm,
    dataTestName: 'catalog-details-ingest-cataloging-managed-uploads',
  },
  {
    label: localizedStrings.CATALOG_PREVIEWS_SHORT,
    route: 'previews',
    component: CatalogPreviewsForm,
    dataTestName: 'catalog-details-ingest-cataloging-previews',
  },
];

export const globalSettingsPages = [
  {
    component: ServerLocationsPage,
    label: localizedStrings.GLOBAL_SETTINGS_SERVER_DATA_LOCATIONS,
    route: '/globalSettings/dataLocations',
    dataTestName: 'global-settings-data-locations',
  },
  {
    component: LoggingControls,
    label: localizedStrings.LOGGING_CONFIGURATION,
    route: '/globalSettings/logging',
    dataTestName: 'global-settings-logging',
  },
  {
    component: LoginCustomizationPage,
    label: localizedStrings.GLOBAL_SETTINGS_WEB_CLIENT_CUSTOMIZATION,
    route: '/globalSettings/customization',
    dataTestName: 'global-settings-customization',
  },
  {
    component: MobilePage,
    label: localizedStrings.MOBILE_TITLE,
    route: '/globalSettings/mobile',
    dataTestName: 'global-settings-mobile',
  },
  {
    component: SingleSignOnCredsPage,
    label: localizedStrings.GLOBAL_SETTINGS_SSO_CREDENTIALS,
    route: '/globalSettings/sso',
    dataTestName: 'global-settings-sso',
  },
  {
    component: EmailNotificationsPage,
    label: localizedStrings.GLOBAL_SETTINGS_EMAIL_CONFIGURATION,
    route: '/globalSettings/email',
    dataTestName: 'global-settings-email',
  },
  {
    component: MediaEnginesPage,
    label: localizedStrings.NAV_MEDIA_ENGINES,
    route: '/globalSettings/mediaEngines',
    dataTestName: 'global-settings-media-engines',
  },
  {
    component: CertsPage,
    label: localizedStrings.CERTS_TITLE,
    route: '/globalSettings/cert',
    dataTestName: 'global-settings-cert',
  },
  {
    component: PortsPage,
    label: localizedStrings.NAV_PORTS,
    route: '/globalSettings/ports',
    dataTestName: 'global-settings-ports',
  },
  // {
  //   component: AnalyticsPage,
  //   label: localizedStrings.DATA_COLLECTION_TITLE,
  //   route: '/globalSettings/analytics',
  //   dataTestName: 'global-settings-analytics',
  // },
];

export const pageRouteRequiresIdentityService = [
  '/catalogs',
  '/users',
  '/roles',
  '/directoryServices',
  '/mobile',
  '/reports',
  '/NetPublish',
];

export const pageRouteRequiresLicense = [
  '/catalogs',
  '/users',
  '/roles',
  '/directoryServices',
  '/mobile',
  '/reports',
  '/NetPublish',
  '/backups',
  '/globalSettings',
];



// WEBPACK FOOTER //
// ./src/view/App/pageData.js